import { Form } from 'react-bootstrap';
import Modal from '../../components/modals/Modal';
import RiskMatrix from '../../components/RiskMatrix';
import { useModal } from '../../providers/ModalProvider';
import { modals } from '../../providers/modals';
import styles from './AddRiskModal.module.scss';
import { Text } from '../../components/typography';
import { loader } from 'graphql.macro';
import { useMutation, useQuery } from '@apollo/client';
import CreatableSelect from 'react-select/creatable';
import TextWithIcon from '../../components/typography/TextWithIcon';
import { useMemo, useState } from 'react';
import {
  mitigationLookup,
  mitigationTypes,
} from '../../utilities/risk_registry';
import Icon from '../../components/Icon';
import { useDocumentControllerActionItem } from '../../hooks/risk_registry/document_controller_action_item';

const addRiskRegistryMutation = loader('./RiskRegistryList.add.graphql');
const addMitigationMutation = loader('./Mitigations.add.graphql');
const mitigationQuery = loader('./Mitigations.fetch.graphql');
const registryCategoryQuery = loader('./NewRiskList.registry.graphql');

export default function AddRiskRegistryModal() {
  const { modalState, closeModal, updateModal } = useModal();
  const [addRiskRegistry] = useMutation(addRiskRegistryMutation, {
    refetchQueries: ['GetRiskRegistryForTable'],
  });
  const [addMitigation] = useMutation(addMitigationMutation);
  const { getRoleControllerIds, createRiskCreatedActionItem } =
    useDocumentControllerActionItem();
  const [threats, setThreats] = useState([]);
  const [consequences, setConsequences] = useState([]);
  const { show, impact, probability, category, description } =
    modalState.addRiskRegistryModal;

  const onHide = () => {
    setThreats([]);
    setConsequences([]);
    setInputValue('');
    closeModal({ modalName: modals.addRiskRegistry });
  };

  const update = (variables) =>
    updateModal({
      modalName: modals.addRiskRegistry,
      variables: { ...variables },
    });
  const {
    data: {
      threats: fetchedThreats = [],
      barriers: fetchedBarriers = [],
      consequences: fetchedConsequences = [],
    } = {},
  } = useQuery(mitigationQuery);
  const { data: { riskRegistry = [] } = {} } = useQuery(registryCategoryQuery);
  const [inputValue, setInputValue] = useState('');

  const filteredCategories = useMemo(() => {
    if (!!inputValue && !!riskRegistry?.length) {
      const searchInput = inputValue.toLowerCase();
      return riskRegistry
        .map((r) => (r.category || '').toLowerCase())
        .filter((category) => category.includes(searchInput));
    }
    return [];
  }, [riskRegistry, inputValue]);

  const handleCreate = (title, type) => {
    const group = mitigationLookup[type];

    addMitigation({
      variables: {
        title,
        type,
      },
    }).then(({ data: { addMitigation } }) => {
      const newMitigation = {
        label: addMitigation.title,
        value: addMitigation.id,
        ...addMitigation,
      };
      if (group === 'threats') {
        setThreats([...threats, newMitigation]);
      } else {
        setConsequences([...consequences, newMitigation]);
      }
    });
  };

  const mitigations = useMemo(() => {
    const threats = fetchedThreats?.map((m) => ({
      label: m.title,
      value: m.id,
    }));

    const barriers = fetchedBarriers?.map((m) => ({
      label: m.title,
      value: m.id,
    }));
    const consequences = fetchedConsequences?.map((m) => ({
      label: m.title,
      value: m.id,
    }));

    return { threats, barriers, consequences };
  }, [fetchedThreats, fetchedBarriers, fetchedConsequences]);

  const submitDisabled = useMemo(
    () => !description || !category || !impact || !probability,
    [description, category, impact, probability],
  );

  return (
    <Modal
      title={'⚠️  Add Hazard to Risk Registry'}
      open={show}
      onClose={onHide}
      submitDisabled={submitDisabled}
      onSubmit={async () => {
        const documentControllerIds = await getRoleControllerIds();
        const { data: { addRiskRegistry: risk } = {} } = await addRiskRegistry({
          variables: {
            ...modalState.addRiskRegistryModal,
            mitigations: [
              ...threats?.map((t) => t.value),
              ...consequences?.map((t) => t.value),
            ],
            documentControllerIds,
          },
        });
        await createRiskCreatedActionItem({
          risk,
          userIds: documentControllerIds,
        });
      }}
    >
      <div className={styles.container}>
        <div className={styles.content}>
          <TextWithIcon required weight="semiBold" noMargin>
            Hazard Category<span className={styles.red}>*</span>
          </TextWithIcon>
          <Form.Control
            placeholder="Enter Category"
            value={category}
            className={!!filteredCategories?.length && styles.highlight}
            onChange={(e) => {
              update({ category: e.target.value });
              setInputValue(e.target.value);
            }}
          />
          {!!filteredCategories?.length ? (
            <div className={styles.dropdown}>
              <Text size="sm" noMargin weight="semiBold">
                Duplicate Categories Found
              </Text>
              <div className={styles.spacer} />
              {filteredCategories?.map((category, index) => (
                <div className={styles.list} key={index}>
                  <Icon style={{ fontSize: '1rem' }} color="red">
                    error_outline
                  </Icon>
                  <Text noMargin>{category}</Text>
                </div>
              ))}
            </div>
          ) : null}
          <TextWithIcon required weight="semiBold" noMargin>
            Description<span className={styles.red}>*</span>
          </TextWithIcon>
          <Form.Control
            placeholder="Enter Description"
            as="textarea"
            rows={3}
            value={description}
            onChange={(e) => update({ description: e.target.value })}
          />
          <TextWithIcon required weight="semiBold" noMargin>
            Threats/Hazards
          </TextWithIcon>
          <CreatableSelect
            isClearable
            onChange={(selected) => {
              setThreats(selected);
            }}
            onCreateOption={(selected) => {
              handleCreate(selected, mitigationTypes.THREAT.key);
            }}
            options={mitigations.threats || []}
            isMulti
            placeholder={`Select or create new threat`}
            value={threats || []}
            closeMenuOnSelect={false}
          />
          <TextWithIcon required weight="semiBold" noMargin>
            Hazard Consequences
          </TextWithIcon>
          <CreatableSelect
            isClearable
            onChange={(selected) => {
              setConsequences(selected);
            }}
            onCreateOption={(selected) => {
              handleCreate(selected, mitigationTypes.CONSEQUENCE.key);
            }}
            options={mitigations.consequences || []}
            isMulti
            placeholder={`Select or create new consequence`}
            value={consequences || []}
            closeMenuOnSelect={false}
          />
          <TextWithIcon required weight="semiBold" noMargin>
            Initial Risk Assessment<span className={styles.red}>*</span>
          </TextWithIcon>
          <RiskMatrix
            showMessage={false}
            showAddToRegistry={false}
            impact={impact}
            probability={probability}
            onChange={(impact, probability) => update({ impact, probability })}
          />
        </div>
      </div>
    </Modal>
  );
}
